import React, { useRef, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  SimpleGrid,
  Text,
  useToast,
} from '@chakra-ui/react';
import { MdDescription, MdInsertDriveFile } from 'react-icons/md';
import { AppCard, OutlineButton, StatusBadge, UploadTile } from '../../../../components/ui';
import {
  deleteDocument,
  uploadDocument,
} from '../../../../repositories/document.repository';

const SIDE_SLOTS = [
  { docType: 'Front', label: 'Front', match: /front/i },
  { docType: 'Rear', label: 'Rear', match: /rear|back/i },
  { docType: 'Left', label: 'Left', match: /left/i },
  { docType: 'Right', label: 'Right', match: /right/i },
];

const SIDE_COLUMNS = { base: 1, sm: 2, md: 4 };

function fileExtBadge(doc) {
  const mime = String(doc?.mimeType || '').toLowerCase();
  const name = String(doc?.fileName || '').toLowerCase();
  if (mime.includes('pdf') || name.endsWith('.pdf')) return 'PDF';
  if (mime.includes('png') || name.endsWith('.png')) return 'PNG';
  if (mime.includes('webp') || name.endsWith('.webp')) return 'WEBP';
  if (mime.includes('gif') || name.endsWith('.gif')) return 'GIF';
  if (mime.includes('jpeg') || mime.includes('jpg') || /\.jpe?g$/.test(name)) return 'JPG';
  if (doc?.kind === 'pdf') return 'PDF';
  if (doc?.kind === 'image') return 'IMG';
  return 'FILE';
}

function matchSlotDoc(docs, slot) {
  const hits = (docs || []).filter((d) => {
    const label = `${d.label || ''} ${d.docType || ''} ${d.fileName || ''}`;
    return slot.match.test(label);
  });
  if (!hits.length) return null;
  return hits.reduce((best, d) => ((d.id || 0) > (best.id || 0) ? d : best), hits[0]);
}

function toViewDoc(slot, raw) {
  if (!raw) return null;
  const mime = String(raw.mimeType || '').toLowerCase();
  const name = String(raw.fileName || '').toLowerCase();
  const isPdf = mime.includes('pdf') || name.endsWith('.pdf') || raw.kind === 'pdf';
  return {
    id: raw.id,
    docType: slot.docType,
    label: slot.label,
    fileName: raw.fileName || 'attachment',
    mimeType: raw.mimeType || '',
    kind: isPdf ? 'pdf' : 'image',
    url: raw.url,
  };
}

/**
 * Four vehicle side photos — same card UI as vendor "Documents attached".
 * Staff can upload / replace on this screen without a full page reload.
 */
export function EquipmentSidePhotosSection({ asset, documents = [], onOpen, onChanged }) {
  const toast = useToast();
  const fileRef = useRef(null);
  const pendingTypeRef = useRef(null);
  const [busyType, setBusyType] = useState('');

  const pick = (slot) => {
    pendingTypeRef.current = slot.docType;
    if (fileRef.current) {
      fileRef.current.accept = 'image/*,application/pdf';
      fileRef.current.click();
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    const docType = pendingTypeRef.current;
    e.target.value = '';
    pendingTypeRef.current = null;
    if (!file || !docType || !asset?.id) return;

    const slot = SIDE_SLOTS.find((s) => s.docType === docType);
    const existing = matchSlotDoc(documents, slot);
    setBusyType(docType);
    try {
      const uploaded = await uploadDocument({
        ownerType: 'asset',
        ownerId: asset.id,
        docType,
        file,
      });
      if (existing?.id && existing.id !== uploaded.id) {
        await deleteDocument(existing.id).catch(() => {});
      }
      toast({
        title: `${slot?.label || 'Photo'} uploaded`,
        status: 'success',
        position: 'top-right',
      });
      await onChanged?.();
    } catch (err) {
      toast({
        title: err?.message || 'Upload failed',
        status: 'error',
        position: 'top-right',
      });
    } finally {
      setBusyType('');
    }
  };

  return (
    <AppCard mb={6}>
      <Heading size={{ base: 'sm', xl: 'md' }} mb={1}>
        4 side photos
      </Heading>
      <Text fontSize={{ base: '13px', xl: '14px' }} color="text.secondary" mb={4}>
        Front, rear, left, and right views. Click a photo to preview. Staff can upload or
        replace here.
      </Text>
      <input
        ref={fileRef}
        type="file"
        accept="image/*,application/pdf"
        hidden
        onChange={handleFile}
      />
      <SimpleGrid columns={SIDE_COLUMNS} spacing={{ base: 3, xl: 4 }}>
        {SIDE_SLOTS.map((slot) => {
          const doc = toViewDoc(slot, matchSlotDoc(documents, slot));
          const busy = busyType === slot.docType;
          const isImage = doc?.kind === 'image' && !!doc?.url;
          const isPdf = doc?.kind === 'pdf';
          return (
            <Box
              key={slot.docType}
              overflow="hidden"
              bg="bg.input"
              borderRadius="sm"
              borderWidth="1px"
              borderColor="border.subtle"
            >
              {doc?.url ? (
                <>
                  <Box
                    as="button"
                    type="button"
                    w="100%"
                    textAlign="left"
                    cursor="pointer"
                    onClick={() => onOpen?.(doc)}
                    sx={{ '& > *': { pointerEvents: 'none' } }}
                    _hover={{ opacity: 0.92 }}
                  >
                    {isImage ? (
                      <Box
                        w="100%"
                        h={{ base: '140px', xl: '160px' }}
                        bg="blackAlpha.50"
                        overflow="hidden"
                      >
                        <Image
                          src={doc.url}
                          alt={slot.label}
                          w="100%"
                          h="100%"
                          objectFit="cover"
                        />
                      </Box>
                    ) : (
                      <Flex align="center" gap={3} p={{ base: 3, xl: 4 }}>
                        <Flex
                          w="44px"
                          h="44px"
                          align="center"
                          justify="center"
                          borderRadius="sm"
                          bg={isPdf ? 'whiteAlpha.100' : 'bg.amberTint'}
                          flexShrink={0}
                        >
                          <Icon
                            as={isPdf ? MdDescription : MdInsertDriveFile}
                            boxSize={6}
                            color="text.secondary"
                          />
                        </Flex>
                      </Flex>
                    )}
                    <Flex align="center" gap={3} p={{ base: 3, xl: 4 }}>
                      <Box minW={0} flex={1}>
                        <Text fontSize={{ base: '14px', xl: '15px' }} fontWeight="600" noOfLines={1}>
                          {slot.label}
                        </Text>
                        <Text
                          fontSize={{ base: '12px', xl: '13px' }}
                          color="text.secondary"
                          noOfLines={1}
                          fontFamily="mono"
                        >
                          {doc.fileName}
                        </Text>
                      </Box>
                      <StatusBadge status="neutral">{fileExtBadge(doc)}</StatusBadge>
                    </Flex>
                  </Box>
                  <HStack px={{ base: 3, xl: 4 }} pb={3} spacing={2}>
                    <OutlineButton size="sm" isLoading={busy} onClick={() => pick(slot)}>
                      Replace
                    </OutlineButton>
                    <OutlineButton size="sm" onClick={() => onOpen?.(doc)} isDisabled={busy}>
                      View
                    </OutlineButton>
                  </HStack>
                </>
              ) : (
                <Box p={{ base: 3, xl: 4 }}>
                  <Text fontSize={{ base: '14px', xl: '15px' }} fontWeight="600" mb={3}>
                    {slot.label}
                  </Text>
                  <UploadTile
                    label={busy ? 'Uploading…' : 'Upload file'}
                    onClick={() => !busy && pick(slot)}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </SimpleGrid>
    </AppCard>
  );
}

export function equipmentFileExtBadge(doc) {
  return fileExtBadge(doc);
}
